import './OrganisationSettings.scss';

import { Form, Formik, Field, FieldProps, FormikProps, FormikErrors } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import countries from 'i18n-iso-countries';
import AsyncSelect from 'react-select/async';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import classnames from 'classnames';
import debounce from 'lodash/debounce';

import {
  addAdminsToOrganisationAPI,
  fetchOrganizationAllAdminsAPI,
  IFetchOrganizationAllAdminsResponse,
  IOrganization,
  removeAdminsFromOrganisationAPI,
  updateOrganizationAPI,
  verifyAvailabilityOrganizationAPI,
  connectOrganizationStripeAPI,
  StripeAccount,
} from '../../apis/organizations';

import { CheckIcon } from '../../assets/icons/CheckIcon';
import { CheckCircleIcon } from '../../assets/icons/CheckCircleIcon';
import { CloseCircle } from '../../assets/icons/CloseCircle';
import { PictureIcon } from '../../assets/icons/PictureIcon';
import {
  ButtonIcon,
  CustomButton,
  FormTextField,
  ReactSelectForm,
  RoundedAvatar,
} from '../../components';
import { InfoImageUpload } from '../tourDetails/Information/components/InfoImageUpload';

import { useGetCurrentOrganization } from '../../features/organizations/organizationsSelectors';
import { Accordion, Card, FormControl, InputGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InfoCircleFill } from 'react-bootstrap-icons';
import { CustomAccordionHeader } from '../tourDetails/common/components/CustomAccordionHeader';
import { imgOptionUserLabel } from '../../utils/imgOptionUserLabel';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { addUsersFromSearch } from '../../features/users/userSlice';
import { fetchUsersData } from '../../apis/tournament';
import { User } from '../../types/tournament';
import { StylesConfig } from 'react-select';
import { updateOrganizationsList } from '../../features/organizations/organizationsSlice';
import { DuprForm, DuprFormValues } from './components/DuprForm';
import { buildCurrencyOptions } from '../../utils/currencyOptions';
import { useLocation } from 'react-router-dom';
import { Notification } from '../../hooks/useNotification';
import { GroupStandingsTieBreakerEnum, TypeError } from '../../types/enums';
import { ToastContainer } from 'react-toastify';
import { GroupStandingsTieBreakerSection } from '../tourDetails/common/components/GroupStandingsTieBreakerEditor';
import {
  defaultGroupStandingsTieBreakersForClass,
  DEFAULT_TEAM_MATCHUP_SCORING,
  hasRequiredGroupStandingsTieBreakers,
  requiredGroupStandingsTieBreakersErrorKey,
  resolveGroupStandingsTieBreakerResolution,
  TeamMatchupScoring,
  toPersistedGroupStandingsTieBreakers,
  toPersistedTeamMatchupScoring,
} from '../../utils/groupStandingsTieBreakers';

type Option = { value: number; label: string };

interface IOrganisationName {
  name: string;
}

interface IOrganisationDescription {
  description?: string;
}

interface IOrganisationContact {
  contactEmail?: string;
  website?: string;
  publicDescription?: string;
  countryCode?: string;
  defaultCurrency?: string;
}

interface IOrganisationPayment {
  vat?: string;
}

interface IOrganisationSettings {
  img?: string;
}

export const customStyles: StylesConfig = {
  control: (base) => ({ ...base, borderRadius: '16px', minHeight: '50px' }),
};

countries.registerLocale(require('i18n-iso-countries/langs/en.json'));
countries.registerLocale(require('i18n-iso-countries/langs/sv.json'));
countries.registerLocale(require('i18n-iso-countries/langs/fr.json'));
countries.registerLocale(require('i18n-iso-countries/langs/es.json'));
countries.registerLocale(require('i18n-iso-countries/langs/it.json'));
countries.registerLocale(require('i18n-iso-countries/langs/pt.json'));

export const OrganisationSettings = () => {
  const prefix = 'communitySettings-';

  const { userData } = useAppSelector((state) => state.users);
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const currentOrganization = useGetCurrentOrganization();
  const [isNameVerified, setIsNameVerified] = useState(true);
  const [multiSelections, setMultiSelections] = useState<IFetchOrganizationAllAdminsResponse[]>([]);
  const [amountOfOptions, setAmountOfOptions] = useState(0);
  const [admins, setAdmins] = useState<IFetchOrganizationAllAdminsResponse[]>([]);
  const [deletedAdmins, setDeletedAdmins] = useState<number[]>([]);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [businessType, setBusinessType] = useState<'company' | 'non_profit' | 'individual' | ''>(
    ''
  );
  const [groupStandingsTieBreakers, setGroupStandingsTieBreakers] = useState<
    GroupStandingsTieBreakerEnum[]
  >(defaultGroupStandingsTieBreakersForClass({}));
  const [useInheritedGroupStandingsTieBreakers, setUseInheritedGroupStandingsTieBreakers] =
    useState(true);
  const [teamMatchupScoring, setTeamMatchupScoring] = useState<TeamMatchupScoring>(
    DEFAULT_TEAM_MATCHUP_SCORING,
  );
  const [useInheritedTeamMatchupScoring, setUseInheritedTeamMatchupScoring] = useState(true);

  useEffect(() => {
    if (!currentOrganization) return;
    const hasExplicitTieBreakers = Boolean(
      currentOrganization.groupStandingsTieBreakers?.length,
    );
    setUseInheritedGroupStandingsTieBreakers(!hasExplicitTieBreakers);
    setGroupStandingsTieBreakers(
      hasExplicitTieBreakers
        ? currentOrganization.groupStandingsTieBreakers!
        : defaultGroupStandingsTieBreakersForClass({}),
    );
    setTeamMatchupScoring(
      currentOrganization.teamMatchupScoring ?? DEFAULT_TEAM_MATCHUP_SCORING,
    );
    setUseInheritedTeamMatchupScoring(currentOrganization.teamMatchupScoring == null);
  }, [currentOrganization]);

  const organizationTieBreakerResolution = useMemo(
    () =>
      resolveGroupStandingsTieBreakerResolution({
        organizationTieBreakers: useInheritedGroupStandingsTieBreakers
          ? null
          : groupStandingsTieBreakers,
        organizationTeamMatchupScoring: useInheritedTeamMatchupScoring
          ? null
          : teamMatchupScoring,
      }),
    [
      groupStandingsTieBreakers,
      teamMatchupScoring,
      useInheritedGroupStandingsTieBreakers,
      useInheritedTeamMatchupScoring,
    ],
  );

  const persistedGroupStandingsTieBreakers = toPersistedGroupStandingsTieBreakers(
    useInheritedGroupStandingsTieBreakers,
    groupStandingsTieBreakers,
  );

  const countryOptions = useMemo(() => {
    const language = i18n.language.substring(0, 2);
    const countriesi18n = countries.getNames(language, { select: 'alias' });
    return Object.entries(countriesi18n)
      .map(([code, name]) => ({ value: code, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [i18n.language]);

  const currencyOptions = useMemo(() => buildCurrencyOptions(i18n.language), [i18n.language]);

  const nameForm = {
    name: currentOrganization?.name || '',
  };

  const nameSchema = Yup.object().shape({
    name: Yup.string().required(),
  });
  const nameFormRef = useRef<FormikProps<IOrganisationName>>(null);
  const handleNameForm = async (value: IOrganisationName) => {
    if (!value.name) return;
    const { isAvailable } = await verifyAvailabilityOrganizationAPI(value);
    if (isAvailable || value.name === currentOrganization?.name) {
      setIsNameVerified(true);
    } else {
      setIsNameVerified(false);
    }

    if (!isAvailable) nameFormRef.current?.setFieldError('name', t(prefix + 'nameIsNotAvailable'));
  };

  const discriptionForm = {
    description: currentOrganization?.description || '',
  };
  const discriptionSchema = Yup.object().shape({
    description: Yup.string(),
    vat: Yup.string(),
  });
  const discriptionFormRef = useRef<FormikProps<IOrganisationDescription>>(null);

  const contactForm = {
    contactEmail: currentOrganization?.contactEmail || '',
    website: currentOrganization?.website?.replace(/^https?:\/\//, '') || '',
    publicDescription: currentOrganization?.publicDescription || '',
    countryCode: currentOrganization?.countryCode || '',
    defaultCurrency: currentOrganization?.defaultCurrency || '',
  };
  const contactSchema = Yup.object().shape({
    contactEmail: Yup.string()
      .email(t(prefix + 'invalidEmail'))
      .optional(),
    website: Yup.string()
      .test('url', t(prefix + 'invalidUrl'), (value) => {
        if (!value) return true; // optional field

        // Validate as domain (user enters without protocol)
        try {
          new URL(`https://${value}`);
          return true;
        } catch {
          return false;
        }
      })
      .optional(),
    publicDescription: Yup.string().optional(),
    countryCode: Yup.string().optional(),
    defaultCurrency: Yup.string().required(t(prefix + 'currencyRequired')),
  });
  const contactFormRef = useRef<FormikProps<IOrganisationContact>>(null);

  const paymentForm = {
    vat: currentOrganization?.vat || '',
  };
  const paymentSchema = Yup.object().shape({
    vat: Yup.string(),
  });
  const paymentFormRef = useRef<FormikProps<IOrganisationPayment>>(null);

  const organisationForm = {
    img: currentOrganization?.img || '',
  };
  const organisationSchema = Yup.object().shape({
    img: Yup.string(),
    description: Yup.string(),
  });
  const imageFormRef = useRef<FormikProps<IOrganisationSettings>>(null);
  const duprFormRef = useRef<FormikProps<DuprFormValues>>(null);

  const getAdmins = async () => {
    const admins =
      currentOrganization && (await fetchOrganizationAllAdminsAPI(currentOrganization?.id));
    setAdmins(admins || ([] as IFetchOrganizationAllAdminsResponse[]));
  };

  const handleSelect = useCallback(
    (e: Option[]) => {
      const userIds = e.map(({ value }) => value);
      setDeletedAdmins(deletedAdmins.filter((i) => !userIds.includes(i)));
      setMultiSelections(
        //@ts-ignore

        [
          ...multiSelections,
          ...e
            .filter(({ value }) => !admins.find(({ id }) => id === value))
            .map((option) => userData.find((user) => user.id === option.value))
            .map((i) => ({ ...i, role: 1 })),
        ]
      );
      selectRef.current.clearValue();
    },
    [deletedAdmins, multiSelections, admins, userData]
  );

  const handleRemove = useCallback(
    (userId: number) => {
      setDeletedAdmins([...deletedAdmins, userId]);
      setMultiSelections(multiSelections.filter(({ id }) => id !== userId));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [multiSelections, deletedAdmins]
  );

  const selectRef = useRef<any>(null);

  const promiseOptions = debounce((inputValue, cb) => {
    fetchUsersData(inputValue)
      .then((response) => {
        setAmountOfOptions(response.length);
        dispatch(addUsersFromSearch(response));
        cb(response.map((user: User) => imgOptionUserLabel(user)));
      })
      .catch((e) => {
        console.log(e);
      });
  }, 500);

  useEffect(() => {
    getAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization]);

  const handleOrganisationForm = async () => {
    if (!currentOrganization) return;

    // Validate name form
    const nameErrors = (await nameFormRef.current?.validateForm(
      nameFormRef.current.values
    )) as FormikErrors<IOrganisationName>;

    if (Object.keys(nameErrors).length) {
      nameFormRef.current?.setTouched({ name: true });
      nameFormRef.current?.setErrors(nameErrors);
      console.log('errors', nameErrors);
      return;
    }

    if (!isNameVerified) {
      nameFormRef.current?.setFieldError('name', t('communityCreate-nameIsNotChecked'));
      console.log({
        isNameVerified,
        nameFormRefName: nameFormRef.current?.values.name,
        currentOrganizationName: currentOrganization?.name,
      });
      console.log('Name is not checked');
      return;
    }

    // Validate contact form
    const contactErrors = (await contactFormRef.current?.validateForm(
      contactFormRef.current?.values
    )) as FormikErrors<IOrganisationContact>;

    if (contactErrors && Object.keys(contactErrors).length) {
      contactFormRef.current?.setTouched({
        contactEmail: true,
        website: true,
        publicDescription: true,
        countryCode: true,
        defaultCurrency: true,
      });
      contactFormRef.current?.setErrors(contactErrors);
      if (contactErrors.defaultCurrency) {
        Notification(TypeError.error, t(prefix + 'currencyMissing'));
      }
      return;
    }

    if (
      !useInheritedGroupStandingsTieBreakers &&
      !hasRequiredGroupStandingsTieBreakers(groupStandingsTieBreakers)
    ) {
      Notification(
        TypeError.error,
        t(requiredGroupStandingsTieBreakersErrorKey(groupStandingsTieBreakers)),
      );
      return;
    }

    const organizationId = currentOrganization.id;
    const removedAdmins = admins.filter(({ id }) => deletedAdmins.includes(id)).map(({ id }) => id);

    const contactValues = { ...(contactFormRef.current?.values || {}) };
    if (contactValues.website) {
      contactValues.website = `https://${contactValues.website}`;
    }

    const organizationPayload: IOrganization = {
      id: currentOrganization.id,
      ...(nameFormRef.current?.values || {}),
      ...(discriptionFormRef.current?.values || {}),
      ...contactValues,
      ...(imageFormRef.current?.values || {}),
      ...(duprFormRef.current?.values || {}),
      ...(paymentFormRef.current?.values || {}),
      groupStandingsTieBreakers: persistedGroupStandingsTieBreakers,
      teamMatchupScoring: toPersistedTeamMatchupScoring(
        useInheritedTeamMatchupScoring,
        teamMatchupScoring,
      ),
    } as IOrganization;

    try {
      await Promise.all([
        updateOrganizationAPI(organizationPayload),
        removedAdmins.length &&
          removeAdminsFromOrganisationAPI({
            organizationId,
            userIds: removedAdmins,
          }),
        multiSelections.length &&
          addAdminsToOrganisationAPI({
            organizationId,
            userIds: multiSelections.map(({ id }) => id),
          }),
      ]);

      dispatch(
        updateOrganizationsList({
          ...currentOrganization,
          id: currentOrganization.id,
          ...(nameFormRef.current?.values || {}),
          ...(discriptionFormRef.current?.values || {}),
          ...contactValues,
          ...(paymentFormRef.current?.values || {}),
          ...(imageFormRef.current?.values || {}),
          ...(duprFormRef.current?.values || {}),
          groupStandingsTieBreakers: persistedGroupStandingsTieBreakers,
          teamMatchupScoring: toPersistedTeamMatchupScoring(
            useInheritedTeamMatchupScoring,
            teamMatchupScoring,
          ),
        } as IOrganization)
      );

      Notification(TypeError.success, t(prefix + 'saveSuccess'));
    } catch {
      Notification(TypeError.error, t(prefix + 'saveError'));
    }
  };

  const handleCancel = () => history.push(`/community/${currentOrganization?.id}`);

  useEffect(() => {
    if (!currentOrganization) return;

    const params = new URLSearchParams(location.search);
    const stripeReturn = params.get('stripe_return');
    const businessTypeFromUrl = params.get('businessType') as
      | 'company'
      | 'non_profit'
      | 'individual'
      | null;

    if (!stripeReturn) return;

    const checkStatus = async () => {
      try {
        setStripeError(null);

        // ✅ Require businessType to be present in URL
        if (!businessTypeFromUrl) {
          setStripeError(t(prefix + 'businessTypeRequired'));
          return;
        }

        const origin = window.location.origin;
        const path = history.location.pathname || window.location.pathname;

        const returnUrl = `${origin}${path}?stripe_return=true&businessType=${businessTypeFromUrl}`;
        const refreshUrl = `${origin}${path}?stripe_return=refresh&businessType=${businessTypeFromUrl}`;

        const vat = paymentFormRef.current?.values?.vat || currentOrganization.vat || undefined;

        const res = await connectOrganizationStripeAPI({
          organizationId: currentOrganization.id,
          returnUrl,
          refreshUrl,
          vat,
          businessType: businessTypeFromUrl,
        });

        if (res.detailsSubmitted) {
          //sync local state so dropdown stays consistent if ever shown again
          setBusinessType(businessTypeFromUrl);
          const updatedStripeAccount = {
            ...(currentOrganization?.stripeAccount || {}),
            details_submitted: true,
          };

          dispatch(
            updateOrganizationsList({
              ...currentOrganization,
              stripeAccount: updatedStripeAccount as StripeAccount,
            })
          );
        } else {
          setStripeError(t(prefix + 'stripeNotFinished'));
        }
      } catch (err) {
        console.error(err);
        setStripeError(t(prefix + 'stripeConnectError'));
      }
    };

    void checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, currentOrganization?.id]);

  const handleStripeConnectClick = async () => {
    if (!currentOrganization) return;

    if (!businessType) {
      setStripeError(t(prefix + 'businessTypeRequired'));
      return;
    }

    setStripeError(null);

    const vat = paymentFormRef.current?.values?.vat;

    const baseUrl = window.location.origin;
    const path = `/community/${currentOrganization.id}/settings`;
    const returnUrl = `${baseUrl}${path}?stripe_return=true&businessType=${businessType}`;
    const refreshUrl = `${baseUrl}${path}?stripe_return=refresh&businessType=${businessType}`;

    try {
      setStripeLoading(true);

      const res = await connectOrganizationStripeAPI({
        organizationId: currentOrganization.id,
        returnUrl,
        refreshUrl,
        vat,
        businessType,
      });

      if (res.detailsSubmitted) {
        const updatedStripeAccount = {
          ...(currentOrganization?.stripeAccount || {}),
          details_submitted: true,
        };
        // Optimistic local update so UI shows "connected"
        dispatch(
          updateOrganizationsList({
            ...currentOrganization,
            stripeAccount: updatedStripeAccount as StripeAccount,
          })
        );
        return;
      }

      window.location.href = res.url;
    } catch (err) {
      console.error(err);
      setStripeError(t(prefix + 'stripeConnectFailed'));
    } finally {
      setStripeLoading(false);
    }
  };

  const isStripeConnected = currentOrganization?.stripeAccount?.details_submitted === true;

  return (
    <div className="organisation-settings">
      <div className="organisation-settings__title">{t(prefix + 'title')}</div>
      <Accordion>
        <Card>
          <Card.Header className="p-0 bg-white border-0 relative">
            <CustomAccordionHeader eventKey="Name">{t(prefix + 'general')}</CustomAccordionHeader>
          </Card.Header>
          <Accordion.Collapse eventKey="Name">
            <Card.Body className="p-2">
              <Formik
                validationSchema={nameSchema}
                initialValues={nameForm}
                onSubmit={handleNameForm}
                innerRef={nameFormRef}
                enableReinitialize={true}
              >
                <Form
                  className="organisation-settings__single-item"
                  onChange={() => setIsNameVerified(false)}
                >
                  <div
                    className={classnames('organisation-settings__single-item_text-field', {
                      valid: isNameVerified,
                    })}
                  >
                    <FormTextField
                      name="name"
                      type="text"
                      className=" "
                      label={t(prefix + 'commName')}
                    />
                  </div>
                  <div className="organisation-settings__single-item_submit">
                    <CustomButton
                      btnText={t(prefix + 'verify')}
                      type="submit"
                      variant="outline-dark"
                    >
                      <CheckIcon w="50px" h="25px" f="#000" />
                    </CustomButton>
                  </div>
                </Form>
              </Formik>
              <Formik
                validationSchema={contactSchema}
                initialValues={contactForm}
                onSubmit={() => {}}
                innerRef={contactFormRef}
                enableReinitialize={true}
              >
                <Form>
                  <div>
                    <FormTextField
                      name="publicDescription"
                      type="text"
                      label={t(prefix + 'descriptionOfCommunity')}
                    />
                  </div>
                  <div>
                    <FormTextField
                      name="contactEmail"
                      type="text"
                      label={t(prefix + 'contactEmail')}
                    />
                  </div>
                  <div className="py-3">
                    <label htmlFor="website" className="font-weight-normal">
                      {t(prefix + 'website')}
                    </label>
                    <Field name="website">
                      {({ field, form }: FieldProps) => (
                        <>
                          <InputGroup>
                            <InputGroup.Text>https://</InputGroup.Text>
                            <FormControl
                              {...field}
                              id="website"
                              type="text"
                              placeholder="www.example.com"
                              className={
                                form.touched.website && form.errors.website ? 'is-invalid' : ''
                              }
                            />
                          </InputGroup>
                          {form.touched.website && form.errors.website && (
                            <div className="field-error text-danger fs-7">
                              {form.errors.website as string}
                            </div>
                          )}
                        </>
                      )}
                    </Field>
                  </div>
                  <ReactSelectForm
                    preselect={false}
                    defaultValue={''}
                    options={countryOptions}
                    label={t(prefix + 'country')}
                    name="countryCode"
                    placeholder={t(prefix + 'selectCountry')}
                  />
                  <ReactSelectForm
                    options={currencyOptions}
                    label={t(prefix + 'defaultCurrency')}
                    name="defaultCurrency"
                    placeholder={t(prefix + 'selectCurrency')}
                  />
                </Form>
              </Formik>
              <Formik
                validationSchema={discriptionSchema}
                initialValues={discriptionForm}
                onSubmit={() => {}}
                innerRef={discriptionFormRef}
                enableReinitialize={true}
              >
                <Form>
                  <div>
                    <FormTextField
                      name="description"
                      type="text"
                      label={
                        <div className="d-flex align-items-center">
                          <span>{t(prefix + 'additionalInfo')}</span>
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{t(prefix + 'additionalInfoTooltip')}</Tooltip>}
                          >
                            <InfoCircleFill className="ms-2" style={{ cursor: 'pointer' }} />
                          </OverlayTrigger>
                        </div>
                      }
                    />
                  </div>
                </Form>
              </Formik>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
      <Accordion>
        <Card>
          <Card.Header className="p-0 bg-white border-0 relative">
            <CustomAccordionHeader eventKey="Payment">
              {t(prefix + 'payment')}
            </CustomAccordionHeader>
          </Card.Header>
          <Accordion.Collapse eventKey="Payment">
            <Card.Body className="organisation-settings__payment">
              {/* 1. Status */}
              <section className="payment-section payment-section--status">
                <div className="organisation-settings__stripe-status-row">
                  <span className="organisation-settings__stripe-status-label">
                    {t(prefix + 'connectedPayoutAccount')}
                  </span>
                  {isStripeConnected ? (
                    <CheckCircleIcon w="20" h="20" f="#34C765" />
                  ) : (
                    <div style={{ color: '#DC3545' }}>
                      <CloseCircle />
                    </div>
                  )}
                </div>
              </section>

              {/* VAT form – editable if empty, disabled if saved */}
              <Formik
                validationSchema={paymentSchema}
                initialValues={paymentForm}
                onSubmit={() => {}}
                innerRef={paymentFormRef}
                enableReinitialize={true}
              >
                <Form>
                  <div>
                    <FormTextField
                      name="vat"
                      type="text"
                      label={t(prefix + 'vat')}
                      disabled={!!currentOrganization?.vat}
                    />
                  </div>
                </Form>
              </Formik>

              {/* Stripe connect controls – only when NOT connected */}
              {!isStripeConnected && (
                <>
                  <div className="organisation-settings__stripe-connect-row">
                    <div className="organisation-settings__stripe-connect-left">
                      <label className="organisation-settings__stripe-business-type-label">
                        {t(prefix + 'businessTypeLabel')}
                      </label>
                      <select
                        className="organisation-settings__stripe-business-type-select"
                        value={businessType}
                        onChange={(e) =>
                          setBusinessType(e.target.value as 'company' | 'non_profit' | 'individual')
                        }
                      >
                        <option value="">{t(prefix + 'businessTypePlaceholder')}</option>
                        <option value="company">{t(prefix + 'businessTypeCompany')}</option>
                        <option value="non_profit">{t(prefix + 'businessTypeNonProfit')}</option>
                        <option value="individual">{t(prefix + 'businessTypeIndividual')}</option>
                      </select>
                    </div>

                    <div className="organisation-settings__stripe-connect-right">
                      <CustomButton
                        btnText={t(prefix + 'connectStripe')}
                        variant="outline-dark"
                        onClick={handleStripeConnectClick}
                        disabled={stripeLoading}
                      />
                    </div>
                  </div>

                  {stripeError && (
                    <div className="field-error text-danger fs-7 organisation-settings__stripe-error">
                      {stripeError}
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
      <Accordion>
        <Card>
          <Card.Header className="p-0 bg-white border-0 relative">
            <CustomAccordionHeader eventKey="Image">{t(prefix + 'images')}</CustomAccordionHeader>
          </Card.Header>
          <Accordion.Collapse eventKey="Image">
            <Card.Body className="p-2">
              <Formik
                validationSchema={organisationSchema}
                initialValues={organisationForm}
                innerRef={imageFormRef}
                onSubmit={handleOrganisationForm}
                enableReinitialize={true}
              >
                <Form>
                  <div>
                    <div>
                      <Field name="img">
                        {({ field, form }: FieldProps) => {
                          return (
                            <div className="organisation-settings__community-image-block">
                              <InfoImageUpload
                                title={t(prefix + 'imageLabel')}
                                setFieldValue={form.setFieldValue}
                                imgUrl={field.value || require('../../assets/images/noImage.png')}
                                savePath="organization"
                                settingData={{ name: field.name }}
                              >
                                <div className="organisation-settings__image-button-section">
                                  <div
                                    className="organisation-settings__image-text-wrapper"
                                    role="textbox"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                  >
                                    {t(prefix + 'imageDesc')}
                                  </div>
                                  <div className="organisation-settings__image-button-wrapper">
                                    <CustomButton
                                      btnText={t(prefix + 'imageButton')}
                                      type="button"
                                      variant="outline-dark"
                                    >
                                      <PictureIcon w="30" />
                                    </CustomButton>
                                  </div>
                                </div>
                              </InfoImageUpload>
                              <div
                                className="organisation-settings__image-text-wrapper_outside"
                                role="textbox"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                              >
                                {t(prefix + 'imageDesc')}
                              </div>
                            </div>
                          );
                        }}
                      </Field>
                    </div>
                  </div>
                </Form>
              </Formik>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>

      <DuprForm duprFormRef={duprFormRef} />
      
      <Accordion>
        <Card>
          <Card.Header className="p-0 bg-white border-0 relative">
            <CustomAccordionHeader eventKey="GroupStandings">
              {t('groupStandingsTieBreaker-title')}
            </CustomAccordionHeader>
          </Card.Header>
          <Accordion.Collapse eventKey="GroupStandings">
            <Card.Body className="p-2">
              <GroupStandingsTieBreakerSection
                value={
                  useInheritedGroupStandingsTieBreakers
                    ? organizationTieBreakerResolution.effective
                    : groupStandingsTieBreakers
                }
                onChange={setGroupStandingsTieBreakers}
                description={t('groupStandingsTieBreaker-description')}
                showTitle={false}
                inheritChecked={useInheritedGroupStandingsTieBreakers}
                onInheritChange={(checked) => {
                  setUseInheritedGroupStandingsTieBreakers(checked);
                  if (checked) {
                    setGroupStandingsTieBreakers(defaultGroupStandingsTieBreakersForClass({}));
                  }
                }}
                inheritLabelKey="groupStandingsTieBreaker-inherit"
                editorDisabled={useInheritedGroupStandingsTieBreakers}
                resolution={organizationTieBreakerResolution}
                chainContextScope="organization"
                scoringValue={teamMatchupScoring}
                onScoringChange={setTeamMatchupScoring}
                inheritScoringChecked={useInheritedTeamMatchupScoring}
                onInheritScoringChange={setUseInheritedTeamMatchupScoring}
                inheritScoringLabelKey="teamMatchupScoring-inherit"
              />
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
      
      <Accordion>
        <Card>
          <Card.Header className="p-0 bg-white border-0 relative">
            <CustomAccordionHeader eventKey="Administration">
              {t(prefix + 'admins')}
            </CustomAccordionHeader>
          </Card.Header>
          <Accordion.Collapse eventKey="Administration">
            <Card.Body className="p-2">
              <div className="organisation-settings__users-block">
                <div className="organisation-settings__row">{t(prefix + 'creator')}</div>
                <div>
                  {admins
                    .filter(({ role }) => role === 0)
                    .map(({ userName, firstName, lastName }) => (
                      <div className="organisation-settings__owner-card organisation-settings__row">
                        {firstName && lastName ? `${firstName} ${lastName}` : `@${userName}`}
                      </div>
                    ))}
                </div>
              </div>
              <div className="organisation-settings__users-block">
                <div className="organisation-settings__row">{t(prefix + 'otherAdmins')}</div>
                <div>
                  <div>
                    {[...admins, ...multiSelections]
                      // Show only admins
                      .filter(({ role }) => role === 1)
                      // Do not include deleted admins
                      .filter(({ id }) => !deletedAdmins.includes(id))
                      .map(({ id, userName, firstName, lastName }) => (
                        <div className="organisation-settings__owner-card organisation-settings__row">
                          <div>
                            {firstName && lastName ? `${firstName} ${lastName}` : `@${userName}`}
                          </div>
                          <ButtonIcon
                            className="raqtButtonLeftIcon"
                            onClick={() => handleRemove(id)}
                          >
                            <CloseCircle />
                          </ButtonIcon>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
              <div>
                <AsyncSelect
                  styles={customStyles}
                  isMulti
                  name="members"
                  ref={selectRef}
                  defaultOptions={true}
                  className={classnames(
                    'organisation-settings__row',
                    'basic-multi-select',
                    'holder-select',
                    {
                      'raqt-select__no-options': !amountOfOptions,
                    }
                  )}
                  classNamePrefix="select"
                  loadOptions={promiseOptions}
                  placeholder={t(prefix + 'select')}
                  //@ts-ignore
                  formatOptionLabel={(data: any) => (
                    <div className="d-flex align-items-center">
                      <RoundedAvatar avatar={data.avatar} alt="User avatar" name="user" />
                      {data.label}
                    </div>
                  )}
                />
                <div
                  className="organisation-settings__add-admin-button organisation-settings__row"
                  role="button"
                  onClick={() => handleSelect(selectRef.current.getValue())}
                >
                  {t(prefix + 'addUser')}
                </div>
              </div>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
      <div className="organisation-settings__button-wrapper">
        <CustomButton
          btnText={t(prefix + 'cancel')}
          variant="outline-dark"
          onClick={handleCancel}
        />

        <CustomButton
          className="organisation-settings__submit-form-button"
          btnText={t(prefix + 'save')}
          onClick={handleOrganisationForm}
        >
          <CheckIcon w="50px" h="25px" f="#000" />
        </CustomButton>
        <ToastContainer />
      </div>
    </div>
  );
};
